import { type CSSProperties, type KeyboardEvent, type PointerEvent, useRef, useState } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@shared/ui'
import type { ProtectedReaderSession } from './flipbook-contracts'
import { useFlipbookPerformanceRenderCount } from './flipbook-performance-diagnostics'
import './flipbook.css'

interface ProgressPreview {
  readonly page: number
  readonly url?: string
}

interface FlipbookProgressProps {
  readonly displayPage: number
  readonly isDesktopSpread: boolean
  readonly loadPreview: (page: number) => Promise<string | null>
  readonly onNavigateToPage: (page: number) => void
  readonly pageAspectRatio: number
  readonly pageImages: Record<number, string>
  readonly session: ProtectedReaderSession
  readonly visiblePageNumbers: readonly number[]
}

function clampPage(page: number, pageCount: number) {
  return Math.min(pageCount, Math.max(1, page))
}

function getProgressPercent(page: number, pageCount: number) {
  return pageCount > 1 ? ((page - 1) / (pageCount - 1)) * 100 : 0
}

function getPageFromPointer(event: PointerEvent<HTMLDivElement>, pageCount: number) {
  const bounds = event.currentTarget.getBoundingClientRect()
  const centerX = bounds.left + bounds.width / 2
  const centerY = bounds.top + bounds.height / 2
  const angle = Math.atan2(event.clientY - centerY, event.clientX - centerX) + Math.PI / 2
  const normalizedAngle = (angle < 0 ? angle + Math.PI * 2 : angle) / (Math.PI * 2)

  return clampPage(Math.round(normalizedAngle * (pageCount - 1)) + 1, pageCount)
}

function getPageLabel(page: number, visiblePageNumbers: readonly number[], pageCount: number) {
  return `${page}${visiblePageNumbers.length > 1 ? `–${Math.min(page + 1, pageCount)}` : ''} / ${pageCount}`
}

export function FlipbookProgress({
  displayPage,
  isDesktopSpread,
  loadPreview,
  onNavigateToPage,
  pageAspectRatio,
  pageImages,
  session,
  visiblePageNumbers,
}: FlipbookProgressProps) {
  useFlipbookPerformanceRenderCount('progress')
  const previewRequestIdRef = useRef(0)
  const previewPageRef = useRef(displayPage)
  const [progressPreview, setProgressPreview] = useState<ProgressPreview | null>(null)
  const [isProgressDragging, setIsProgressDragging] = useState(false)
  const previewPage = progressPreview?.page ?? displayPage
  const progressPercent = getProgressPercent(isProgressDragging ? previewPage : displayPage, session.pageCount)
  const previewUrl = progressPreview?.url ?? pageImages[previewPage]
  const previewIsSpread = isDesktopSpread && previewPage > 1 && !(session.pageCount % 2 === 0 && previewPage === session.pageCount)
  const previewNextUrl = previewIsSpread ? pageImages[previewPage + 1] : undefined
  const pageLabel = getPageLabel(previewPage, visiblePageNumbers, session.pageCount)
  const progressLabel = `${Math.round(progressPercent)}%`

  const requestPreview = (page: number) => {
    const requestId = previewRequestIdRef.current + 1
    previewRequestIdRef.current = requestId
    previewPageRef.current = page
    setProgressPreview({ page, url: pageImages[page] })
    void loadPreview(page).then((nextUrl) => {
      if (previewRequestIdRef.current === requestId && nextUrl) {
        setProgressPreview({ page, url: nextUrl })
      }
    })
  }

  const handleProgressPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!event.isPrimary) {
      return
    }

    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    setIsProgressDragging(true)
    requestPreview(displayPage)
  }

  const handleProgressPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!isProgressDragging || !event.isPrimary) {
      return
    }

    event.preventDefault()
    const page = getPageFromPointer(event, session.pageCount)
    if (page !== previewPageRef.current) {
      requestPreview(page)
    }
  }

  const handleProgressPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (isProgressDragging) {
      onNavigateToPage(previewPageRef.current)
    }
    previewRequestIdRef.current += 1
    setIsProgressDragging(false)
    setProgressPreview(null)
  }

  const handleProgressPointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    previewRequestIdRef.current += 1
    setIsProgressDragging(false)
    setProgressPreview(null)
  }

  const handleProgressKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const page = event.key === 'Home'
      ? 1
      : event.key === 'End'
        ? session.pageCount
        : event.key === 'ArrowLeft' || event.key === 'ArrowDown'
          ? displayPage - 1
          : event.key === 'ArrowRight' || event.key === 'ArrowUp'
            ? displayPage + 1
            : null

    if (page === null) {
      return
    }

    event.preventDefault()
    onNavigateToPage(clampPage(page, session.pageCount))
  }

  const progressStyle = { '--flipbook-progress': `${progressPercent}%` } as CSSProperties

  return (
    <div className="flipbookProgress">
      {isProgressDragging ? (
        <div className="flipbookProgressPreview" style={{ width: previewNextUrl ? '10rem' : '6.5rem' }}>
          <div className="flipbookProgressPreviewMedia" style={{ aspectRatio: previewNextUrl ? `${pageAspectRatio * 2}` : `${pageAspectRatio}` }}>
            {previewUrl ? (
              <img alt="" className={previewNextUrl ? 'flipbookProgressPreviewImage isSplit' : 'flipbookProgressPreviewImage'} draggable={false} src={previewUrl} />
            ) : <span aria-label="Loading preview" className="flipbookProgressSpinner" role="progressbar" />}
            {previewNextUrl ? <img alt="" className="flipbookProgressPreviewImage isSplit" draggable={false} src={previewNextUrl} /> : null}
          </div>
          <p className="flipbookProgressPreviewLabel">
            Trang {previewPage}{previewIsSpread && previewPage < session.pageCount ? `–${previewPage + 1}` : ''}
          </p>
        </div>
      ) : null}
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            aria-label="PDF page position"
            aria-valuemax={session.pageCount}
            aria-valuemin={1}
            aria-valuenow={isProgressDragging ? previewPage : displayPage}
            aria-valuetext={pageLabel}
            className="flipbookProgressCircle"
            onKeyDown={handleProgressKeyDown}
            onPointerCancel={handleProgressPointerCancel}
            onPointerDown={handleProgressPointerDown}
            onPointerMove={handleProgressPointerMove}
            onPointerUp={handleProgressPointerUp}
            role="slider"
            style={progressStyle}
            tabIndex={0}
          >
            <span className="flipbookProgressCircleLabel">
              {progressLabel}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>Trang {pageLabel} · {progressLabel}</TooltipContent>
      </Tooltip>
    </div>
  )
}
